'use client';

import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { CookieConsentProvider } from '@web-store-platforms/components/cookie-consent/provider';
import { LoadingProvider } from '@/providers/LoadingProvider';
import { useAuthBoot } from '../context/AuthBootContext';
import { isFromCampaign } from '@/lib/utils/url';
import { getBackgroundStyleByRoute } from '@/lib/utils/style';
import { useAppState } from '@/state/hooks/app.state.hook';
import analyticsService from '@/lib/services/analytics';
import { cookieConsentStorageAdapter } from '@/lib/services/cookieConsentStorage';
import { useLocalizationState } from '@/hooks/useLocalizationState';
import { LanguageProvider } from '@/providers/LanguageProvider';
import { StoreBackground } from '@/components/StoreBackground';
import { cn } from '@/lib/utils/cn';
import { scheduleDeferredClientTask } from '@/lib/utils/deferred';
import { EnsureStoreFonts } from '@/components/EnsureStoreFonts';
import { LoginLoaderInitializer } from './LoginLoaderInitializer';
import { DeferredCookieConsentBanner } from './DeferredCookieConsentBanner';
import { resolveMaintenanceModeEnabled } from '@/lib/utils/maintenanceMode';
import { InfoModal } from '@/components/Footer/components/InfoModal';
import { LoginConsentGate } from './Consent/LoginConsentGate';
import { LoginFooter } from './Consent/LoginFooter';

const MaintenancePage = dynamic(
  () => import('./LoginMaintenancePage').then((module) => module.LoginMaintenancePage),
  { ssr: false }
);
const RotateDevicePromptWrapper = dynamic(
  () => import('./LoginRotateDevicePrompt').then((module) => module.RotateDevicePromptWrapper),
  { ssr: false }
);

/** Background tabs throttle/never fire rAF — reveal the footer anyway. */
const FOOTER_REVEAL_FALLBACK_MS = 2000;
const ROTATE_PROMPT_REVEAL_TIMEOUT_MS = 4000;

export function LoginRootWrapper({ children }: React.PropsWithChildren): React.ReactElement {
  const { authBoot } = useAuthBoot();
  const pathname = usePathname();
  const localizationState = useLocalizationState();
  const [showFooter, setShowFooter] = useState(false);
  const [showRotatePrompt, setShowRotatePrompt] = useState(false);
  const isMaintenanceActive = resolveMaintenanceModeEnabled(authBoot);
  const { backgroundProps } = useAppState();
  const routeBackgroundProps = useMemo(
    () => getBackgroundStyleByRoute(authBoot, pathname),
    [authBoot, pathname]
  );

  useEffect(() => {
    // Reveal the footer the frame after first paint, not after a fixed
    // multi-second timer: it is below-the-fold text and never the largest
    // painted element, so painting it right after the main content is free
    // and removes the multi-second gap. Double rAF = "after the first painted
    // frame"; the timeout is the background-tab fallback where rAF is
    // throttled.
    const revealFooter = () => setShowFooter(true);
    let raf1: number | undefined;
    let raf2: number | undefined;
    if (typeof requestAnimationFrame === 'function') {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(revealFooter);
      });
    }
    const footerFallback = window.setTimeout(revealFooter, FOOTER_REVEAL_FALLBACK_MS);
    const cancelRotate = scheduleDeferredClientTask(() => {
      setShowRotatePrompt(true);
    }, ROTATE_PROMPT_REVEAL_TIMEOUT_MS);
    return () => {
      if (raf1 !== undefined) {
        cancelAnimationFrame(raf1);
      }
      if (raf2 !== undefined) {
        cancelAnimationFrame(raf2);
      }
      clearTimeout(footerFallback);
      cancelRotate();
    };
  }, []);

  if (isMaintenanceActive) {
    return <MaintenancePage />;
  }

  // No QueryClientProvider on main /login — no useQuery consumers here.
  // OTP/username subroutes that need react-query mount their own provider.
  return (
    <CookieConsentProvider
      skipBanner={isFromCampaign()}
      storageAdapter={cookieConsentStorageAdapter}
      onEnableAnalytics={() => analyticsService.optIn()}
      onDisableAnalytics={() => analyticsService.optOut()}
    >
      <LoadingProvider>
        <LoginLoaderInitializer />
        {/* Restore the fonts the login critical path skips (theme +
                    shared-ui families, icon kits, self-hosted fonts.css) after
                    first paint, so the content is not held behind a
                    render-blocking font request. */}
        <EnsureStoreFonts />
        <LanguageProvider localizationState={localizationState}>
          <LoginConsentGate>
            {showRotatePrompt ? <RotateDevicePromptWrapper /> : null}
            <StoreBackground {...(backgroundProps ?? routeBackgroundProps)}>
              <div className={cn('flex min-h-screen flex-col')} data-testid="login-root-wrapper">
                <div className={cn('flex flex-1 flex-col')}>{children}</div>
                {showFooter ? <LoginFooter /> : null}
              </div>
            </StoreBackground>
            <DeferredCookieConsentBanner />
            <InfoModal />
          </LoginConsentGate>
        </LanguageProvider>
      </LoadingProvider>
    </CookieConsentProvider>
  );
}
